import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, Field, G_DARK, Input, PasswordInput, REGIONS } from "../../components/Shared";
import { rememberFocus } from "../../focus";
import { SuccessNote } from "./parts";

// Management -> Storage: the ONE S3 bucket the whole portal stores its files in.
// GET/PATCH /api/admin/storage, POST /api/admin/storage/probe. After data-portal's
// storage panel (GroupStoragePanel.jsx), but one bucket for the whole portal.
//
// - The secret access key is write-only (`secret_access_key_set`); blank keeps the stored one.
// - Saving checks the bucket answers first (head_bucket on the merged values). A refusal is the
//   server's classified sentence, shown verbatim, and nothing is saved.
// - Moving the portal to another bucket or endpoint while files are stored is refused (409)
//   unless the request says `repoint: true`; nothing is copied. The checkbox that sends it only
//   appears after that refusal, so it is read in context.
// - Large files straight to S3 (POST /api/admin/storage/cors-probe): the public endpoint browsers
//   reach, the size from which the SPA uploads there (0 = off), and a check that the bucket's
//   CORS lets browsers PUT parts from EVERY address unio serves: the server probes the portal's
//   address and, with project addresses on, an example project address, whichever page asks
//   (N7, 2026-10-13). On a failure it shows the rule and the aws command for the bucket's
//   administrator; unio never applies it. Turn the size on only after the check passes.

const URL = "/admin/storage";
const FIELDS = ["endpoint_url", "bucket", "region", "access_key_id", "key_prefix", "public_endpoint_url"];

function formFrom(s) {
  return { endpoint_url: s.endpoint_url ?? "", bucket: s.bucket ?? "", region: s.region ?? "",
           access_key_id: s.access_key_id ?? "", key_prefix: s.key_prefix ?? "", secret_access_key: "",
           public_endpoint_url: s.public_endpoint_url ?? "",
           direct_upload_min_mib: String(s.direct_upload_min_mib ?? 0) };
}

export function storageChanges(form, stored) {
  const before = formFrom(stored);
  const out = {};
  for (const k of FIELDS) {
    const v = form[k].trim();
    if (v !== before[k]) out[k] = v;
  }
  if (form.secret_access_key) out.secret_access_key = form.secret_access_key;
  const mib = form.direct_upload_min_mib.trim();
  if (mib !== before.direct_upload_min_mib) out.direct_upload_min_mib = /^\d+$/.test(mib) ? Number(mib) : mib;
  return out;
}

export default function StorageCard() {
  const [cfg, setCfg] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(null);           // null | "save" | "probe"
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [probe, setProbe] = useState(null);
  const [cors, setCors] = useState(null);
  const [needsRepoint, setNeedsRepoint] = useState(false);
  const [repoint, setRepoint] = useState(false);
  const buttonsRef = useRef(null);

  useEffect(() => {
    let live = true;
    api.get(URL)
      .then(c => { if (live) { setCfg(c); setForm(formFrom(c)); } })
      .catch(e => { if (live) setLoadError(e); });
    return () => { live = false; };
  }, []);

  if (loadError) return <ErrorNote error={loadError} />;
  if (!cfg) return <Loading />;

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError(null);
    setNotice(null);
    setProbe(null);
    const payload = storageChanges(form, cfg);
    if (Object.keys(payload).length === 0) { setNotice("Nothing has changed."); return; }
    if (repoint) payload.repoint = true;
    const restore = rememberFocus();
    setBusy("save");
    try {
      const updated = await api.patch(URL, payload);
      setCfg(updated);
      setForm(formFrom(updated));
      setNeedsRepoint(false);
      setRepoint(false);
      setNotice("Saved. The bucket answered, so it is reachable with these credentials.");
    } catch (e) {
      setError(e);
      if (e.status === 409) setNeedsRepoint(true);
    } finally {
      setBusy(null);
      restore(() => buttonsRef.current && buttonsRef.current.querySelector("button"));
    }
  };

  const check = async () => {
    setError(null);
    setNotice(null);
    setProbe(null);
    const restore = rememberFocus();
    setBusy("probe");
    try {
      const r = await api.post(`${URL}/probe`, storageChanges(form, cfg));
      setProbe({ ok: !!r?.ok, detail: r?.detail || "" });
    } catch (e) {
      setProbe({ ok: false, detail: e.message });
    } finally {
      setBusy(null);
      restore();
    }
  };

  const checkCors = async () => {
    setCors(null);
    const restore = rememberFocus();
    setBusy("cors");
    try {
      const r = await api.post(`${URL}/cors-probe`, {});
      setCors({ ok: !!r?.ok, detail: r?.detail || "", results: r?.results || [],
                rule: r?.rule_json ? JSON.stringify(r.rule_json, null, 2) : "", command: r?.command || "" });
    } catch (e) {
      setCors({ ok: false, detail: e.message, results: [], rule: "", command: "" });
    } finally {
      setBusy(null);
      restore();
    }
  };

  const regionOptions = [...new Set([...(cfg.region ? [cfg.region] : []), ...REGIONS])];

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Storage</h2>
        <Badge color={cfg.configured ? "green" : "gray"}>
          <span aria-hidden="true" style={{ marginRight: 4 }}>{cfg.configured ? "✓" : "○"}</span>
          {cfg.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#4b5563" }}>
        Every uploaded file of every project is stored in this one S3 bucket (MinIO or any
        S3-compatible service). Until it is connected, uploads, downloads and builds answer that
        storage is not connected yet.
        {cfg.configured && ` ${cfg.files_stored} file${cfg.files_stored === 1 ? " is" : "s are"} stored in it now.`}
      </p>

      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        <Field label="Endpoint URL" hint="e.g. https://s3.example.org. Blank means AWS S3.">
          <Input value={form.endpoint_url} onChange={set("endpoint_url")} spellCheck={false} />
        </Field>
        <Field label="Bucket" required>
          <Input value={form.bucket} onChange={set("bucket")} spellCheck={false} />
        </Field>
        <Field label="Region" hint="MinIO accepts any; us-east-1 when blank.">
          <Input value={form.region} onChange={set("region")} list="unio-s3-regions" spellCheck={false} />
        </Field>
        <Field label="Key prefix"
               hint="Every object unio writes starts with it, e.g. unio or binf/unio; blank = the bucket root. Changing it moves nothing: stored files keep their place.">
          <Input value={form.key_prefix} onChange={set("key_prefix")} spellCheck={false} />
        </Field>
        <Field label="Access key ID" required>
          <Input value={form.access_key_id} onChange={set("access_key_id")} spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="Secret access key" required={!cfg.secret_access_key_set}
               hint={cfg.secret_access_key_set ? "Currently set — leave blank to keep it" : "Not set"}>
          <PasswordInput value={form.secret_access_key} onChange={set("secret_access_key")} autoComplete="new-password"
                         placeholder={cfg.secret_access_key_set ? "•••••••• (unchanged)" : ""} />
        </Field>
      </div>
      <h3 style={{ margin: "20px 0 4px", fontSize: 15, fontWeight: 700 }}>Large files straight to storage</h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#4b5563" }}>
        Files from the size below go from the browser directly into the bucket, in parts, and can be
        resumed after a dropped connection; the portal only signs the parts and checks the stored file.
        The bucket needs a CORS rule that lets browsers upload from the portal and from every project
        address. The bucket is external: its administrator sets the rule. Check it before turning this on.
      </p>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        <Field label="Public endpoint URL"
               hint="Where browsers reach the bucket, if not at the endpoint above (e.g. http://localhost:9000 for a compose MinIO). Blank = the endpoint.">
          <Input value={form.public_endpoint_url} onChange={set("public_endpoint_url")} spellCheck={false} />
        </Field>
        <Field label="Direct uploads from (MiB)" hint="0 = off: every file goes through the portal.">
          <Input value={form.direct_upload_min_mib} onChange={set("direct_upload_min_mib")} inputMode="numeric" />
        </Field>
      </div>
      <div style={{ marginTop: 12 }}>
        <Btn variant="secondary" onClick={checkCors} disabled={!!busy || !cfg.configured}>
          {busy === "cors" ? "Checking browser uploads…" : "Check browser uploads (CORS)"}
        </Btn>
      </div>
      {cors && <CorsResult cors={cors} bucket={cfg.bucket} />}
      <datalist id="unio-s3-regions">
        {regionOptions.map(r => <option key={r} value={r} />)}
      </datalist>

      {needsRepoint && (
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 16, fontSize: 13, color: "#92400e" }}>
          <input type="checkbox" checked={repoint} onChange={e => setRepoint(e.target.checked)} />
          Move the portal to the new bucket or endpoint anyway. The files stored so far stay where
          they are and are not copied.
        </label>
      )}

      <div ref={buttonsRef} style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Btn onClick={save} disabled={!!busy}>{busy === "save" ? "Checking the bucket…" : "Save"}</Btn>
        <Btn variant="secondary" onClick={check} disabled={!!busy}>
          {busy === "probe" ? "Checking the bucket…" : "Check without saving"}
        </Btn>
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 12, color: "#4b5563" }}>
        Saving checks the bucket answers first: a mistyped endpoint would otherwise be found by the
        first researcher to upload.
      </p>
      {probe && (
        <div role="status" style={{ marginTop: 10, fontSize: 13, color: probe.ok ? G_DARK : "#b91c1c" }}>
          <span aria-hidden="true">{probe.ok ? "✓ " : "✗ "}</span>
          {probe.detail || (probe.ok ? "The bucket answered." : "The bucket did not answer.")}
        </div>
      )}
    </Card>
  );
}

function CopyBlock({ label, button, text }) {
  const [note, setNote] = useState("");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setNote("Copied.");
    } catch {
      setNote("Could not copy — select the text and copy it by hand.");
    }
  };
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 13 }}>{label}</strong>
        <Btn variant="secondary" onClick={copy}>{button}</Btn>
        <span role="status" style={{ fontSize: 12, color: "#4b5563" }}>{note}</span>
      </div>
      <pre aria-label={label} tabIndex={0}
           style={{ margin: "6px 0 0", padding: 10, background: "#f3f4f6", borderRadius: 6, fontSize: 12,
                    whiteSpace: "pre-wrap", wordBreak: "break-all", maxWidth: "100%", overflowX: "auto" }}>
        {text}
      </pre>
    </div>
  );
}

// The answer of POST /admin/storage/cors-probe: one line per address, and on a failure the rule
// for the bucket's administrator (JSON and aws command, built together by the server).
function CorsResult({ cors, bucket }) {
  return (
    <div style={{ marginTop: 10, fontSize: 13 }}>
      <div role={cors.ok ? "status" : "alert"} aria-label="Browser upload check"
           style={{ color: cors.ok ? G_DARK : "#b91c1c" }}>
        <span aria-hidden="true">{cors.ok ? "✓ " : "✗ "}</span>
        {cors.detail}
      </div>
      {cors.results.length > 0 && (
        <ul aria-label="Addresses checked" style={{ margin: "8px 0 0", paddingLeft: 0, listStyle: "none" }}>
          {cors.results.map(r => (
            <li key={r.origin} style={{ marginTop: 4, color: r.ok ? G_DARK : "#b91c1c" }}>
              <span aria-hidden="true">{r.ok ? "✓ " : "✗ "}</span>
              <code>{r.origin}</code>: {r.ok ? "passes" : "fails"}
              <span style={{ color: "#4b5563" }}> — {r.detail}</span>
            </li>
          ))}
        </ul>
      )}
      {!cors.ok && cors.rule && (
        <div style={{ marginTop: 10, color: "#111827" }}>
          <p style={{ margin: 0 }}>
            Send this to the administrator of bucket <code>{bucket}</code>. On a shared bucket the rule is
            added to the rules already there, not put in their place: read them first with
            {" "}<code>aws s3api get-bucket-cors</code>.
          </p>
          <CopyBlock label="CORS rule (JSON)" button="Copy the rule" text={cors.rule} />
          {cors.command && <CopyBlock label="Command" button="Copy the command" text={cors.command} />}
        </div>
      )}
    </div>
  );
}
